import { CSSProperties, FC, ReactNode } from "react";
import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Add } from "@mui/icons-material";
import { makeSxStyles } from "ui";

const DraggableTableRow: FC<{
  children: ReactNode;
  canAddRowOnHover?: boolean;
  id: string | number;
  style?: CSSProperties;
  activeRow: number | null;
  index: number;
  onAddClick?: (index: number) => void;
  disabled?: boolean;
  routeData?: {
    state: "planning" | "planned" | "live" | "ended";
    availableForAnyDriver?: boolean;
    hasNoDriver?: boolean;
  };
  tableWidth?: string;
}> = ({
  children,
  canAddRowOnHover,
  id,
  style,
  activeRow,
  index,
  onAddClick,
  disabled = false,
  routeData,
  tableWidth,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    data: {
      type: "row",
    },
  });

  const styles = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sxStyles = useStyles();

  // Check if we should apply red border (live phase + driver set to Any or No Driver)
  const shouldApplyRedBorder =
    routeData?.state === "live" &&
    (routeData?.availableForAnyDriver === true ||
      routeData?.hasNoDriver === true);

  // Construct sx styles properly
  const getTableRowSx = () => {
    const baseSx = { ...sxStyles.tableRow };

    if (shouldApplyRedBorder) {
      Object.assign(baseSx, sxStyles.redBorderRow);
    }

    if (tableWidth) {
      Object.assign(baseSx, { width: tableWidth, minWidth: tableWidth });
    }

    return baseSx;
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={styles}
      hover
      selected={activeRow === index}
      sx={getTableRowSx()}
    >
      <TableCell
        component="th"
        scope="row"
        padding="none"
        sx={{ width: "48px" }}
      >
        <div
          {...(disabled ? {} : { ...attributes, ...listeners })}
          style={{ position: "relative" }}
        >
          <Box
            sx={{
              cursor: disabled ? "not-allowed" : "grab",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              color: disabled ? "grey.400" : "white",
              "&:hover": {
                color: disabled ? "grey.400" : "grey.300",
              },
            }}
          >
            ⋮⋮
          </Box>
          {canAddRowOnHover && (
            <IconButton
              className="icon-button"
              sx={sxStyles.iconButton}
              onClick={() => onAddClick?.(index)}
            >
              <Add sx={sxStyles.icon} />
            </IconButton>
          )}
        </div>
      </TableCell>
      {children}
    </TableRow>
  );
};

const useStyles = makeSxStyles(({ palette: { primary } }) => ({
  tableRow: {
    "&:hover .icon-button": {
      display: "flex",
    },
    "&:hover": {
      boxShadow: `inset 0px -1px 0px 0px ${primary.main}, 0px 1px 0px 0px ${primary.main}`,
    },
    "&:hover .MuiTableCell-root": {
      borderBottomColor: `${primary.main} !important`,
    },
  },
  redBorderRow: {
    border: "1px solid #BA1A1A !important",
    "& .MuiTableCell-root": {
      borderTop: "1px solid #BA1A1A !important",
      borderBottom: "1px solid #BA1A1A !important",
      "&:first-of-type": {
        borderLeft: "1px solid #BA1A1A !important",
      },
      "&:last-of-type": {
        borderRight: "1px solid #BA1A1A !important",
      },
    },
    "&:hover": {
      border: "1px solid #BA1A1A !important",
      boxShadow: `inset 0px -1px 0px 0px ${primary.main}, 0px 1px 0px 0px ${primary.main}`,
      "& .MuiTableCell-root": {
        borderTop: "1px solid #BA1A1A !important",
        borderBottom: "1px solid #BA1A1A !important",
        "&:first-of-type": {
          borderLeft: "1px solid #BA1A1A !important",
        },
        "&:last-of-type": {
          borderRight: "1px solid #BA1A1A !important",
        },
      },
    },
  },
  iconButton: {
    position: "absolute",
    display: "none",
    left: 0,
    width: "16px",
    height: "16px",
    marginTop: "4px",
    backgroundColor: primary.main,
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: primary.main,
      opacity: "70%",
    },
  },
  icon: {
    width: "10px",
    height: "10px",
    fill: "black",
  },
}));

export default DraggableTableRow;
